import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { postBodyHtml, postExcerpt, postReadTime, postTitle } from '../utils/postContent';
import PixieDust from '../components/PixieDust';
import { Reveal } from '../utils/useReveal';
import './BlogPost.css';

const S3_BASE = 'https://chrystal-mousebound-site-prod-113462084414-ca-central-1.s3.ca-central-1.amazonaws.com';

const ArrowLeft = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16" aria-hidden="true">
    <line x1="19" y1="12" x2="5" y2="12"/>
    <polyline points="12 19 5 12 12 5"/>
  </svg>
);

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [morePosts, setMorePosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const res = await fetch(`${S3_BASE}/blog-posts/metadata/${slug}.json`);
        if (!res.ok) { setNotFound(true); return; }
        const data = await res.json();
        setPost(data);
        document.title = `${postTitle(data.title)} | Magically Mousebound`;
      } catch {
        setNotFound(true);
      } finally {
        setIsLoading(false);
      }
    };
    // Other posts for the "keep reading" strip. Failures here are silent — a
    // missing strip is fine, a broken article page is not.
    const fetchMorePosts = async () => {
      try {
        const manifestRes = await fetch(`${S3_BASE}/blog-posts/manifest.json`);
        if (!manifestRes.ok) return;
        const { posts: slugs } = await manifestRes.json();

        const settled = await Promise.allSettled(
          slugs
            .filter(s => s !== slug)
            .map(s => fetch(`${S3_BASE}/blog-posts/metadata/${s}.json`).then(r => {
              if (!r.ok) throw new Error('missing');
              return r.json();
            }))
        );

        const found = settled
          .filter(r => r.status === 'fulfilled')
          .map(r => r.value)
          .sort((a, b) => new Date(b.date) - new Date(a.date))
          .slice(0, 3);

        setMorePosts(found);
      } catch {
        setMorePosts([]);
      }
    };

    fetchPost();
    fetchMorePosts();
    return () => { document.title = 'Magically Mousebound'; };
  }, [slug]);

  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric'
  });

  if (isLoading) {
    return (
      <div className="blogpost-loading" aria-live="polite">
        <div className="blogpost-spinner" aria-hidden="true" />
        <p>Loading…</p>
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="blogpost-notfound">
        <h1>Post Not Found</h1>
        <p>This article doesn't exist or may have been moved.</p>
        <Link to="/blog" className="blogpost-back-btn"><ArrowLeft /> Back to Blog</Link>
      </div>
    );
  }

  const htmlContent = postBodyHtml(post);
  const readTime = postReadTime(post);

  return (
    <article className="blogpost-page">
      {/* Hero */}
      <div className="blogpost-hero">
        <PixieDust count={9} className="pixie-dust--short" />
        {post.image && (
          <div className="blogpost-hero-image">
            <img src={post.image} alt={`Cover for ${postTitle(post.title)}`} />
            <div className="blogpost-hero-overlay" aria-hidden="true" />
          </div>
        )}
        <div className="blogpost-hero-content container">
          <Link to="/blog" className="blogpost-back-btn">
            <ArrowLeft /> All Articles
          </Link>
          <div className="blogpost-meta">
            <span className="blogpost-category">{post.category}</span>
            {readTime && (
              <span className="blogpost-read-time">{readTime}</span>
            )}
          </div>
          <h1 className="blogpost-title">{postTitle(post.title)}</h1>
          <time className="blogpost-date" dateTime={post.date}>{formatDate(post.date)}</time>
        </div>
      </div>

      {/* Body */}
      <div className="blogpost-body container">
        <div
          className="blogpost-content"
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />

        {/* Footer nav */}
        <div className="blogpost-footer">
          <Link to="/blog" className="blogpost-back-btn blogpost-back-btn--footer">
            <ArrowLeft /> Back to Blog
          </Link>
        </div>
      </div>

      {/* Keep reading — the magic comes back where we want them pulled onward */}
      {morePosts.length > 0 && (
        <section className="keep-reading" aria-label="More articles">
          <PixieDust count={8} className="pixie-dust--short" />
          <div className="container">
            <Reveal as="h2" className="keep-reading-title rule-draw rule-draw--center">
              Keep Reading
            </Reveal>
            <div className="keep-reading-grid" role="list">
              {morePosts.map((other, i) => (
                <Reveal key={other.slug} delay={(i % 3) + 1} role="listitem">
                  <Link to={`/blog/${other.slug}`} className="keep-reading-card">
                    <div className="keep-reading-image">
                      <img src={other.image} alt={`Cover for ${postTitle(other.title)}`} loading="lazy" />
                    </div>
                    <div className="keep-reading-body">
                      <span className="keep-reading-cat">{other.category}</span>
                      <h3>{postTitle(other.title)}</h3>
                      <p>{postExcerpt(other, 110)}</p>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
};

export default BlogPost;
